import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { Spinner } from '../../../shared/components/Spinner';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchMerchantById } from '../../../services/merchants/merchantApi';
import { deleteMerchant } from '../../../services/merchants/adminMerchantApi';
import type { Merchant, MerchantCompanyInfo } from '../../../models/merchant';
import { DeleteMerchantModal } from '../components/DeleteMerchantModal';

function recordDisplayName(m: Merchant): string {
  const business = m.Merchant?.Name?.trim();
  if (business) {
    return business;
  }
  const person = [m.FirstName, m.LastName].filter(Boolean).join(' ').trim();
  if (person) {
    return person;
  }
  return m.Email?.trim() || m.Id;
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="bo-detail-field">
      <span className="bo-detail-field-label">{label}</span>
      <span className="bo-detail-field-value">{children}</span>
    </div>
  );
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

function MerchantLogo({ url, name }: { url: string; name: string }): React.ReactElement {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="bo-merchant-logo-fallback" aria-hidden>
        <span className="bo-merchant-logo-fallback-text">{name.slice(0, 2).toUpperCase()}</span>
      </div>
    );
  }
  return (
    <img
      className="bo-merchant-logo-img"
      src={url}
      alt={`${name} logo`}
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

function MerchantCompanyCard({ merchant }: { merchant: MerchantCompanyInfo }): React.ReactElement {
  const logoUrl = merchant.LogoUrl?.trim();
  const showLogo = Boolean(logoUrl);

  const fields: [string, unknown][] = [
    ['Merchant ID', merchant.MerchantId],
    ['Company ID', merchant.CompanyId],
    ['Email', merchant.Email],
    ['Phone', merchant.Phone],
    ['Address', merchant.AddressLine1],
    ['Address (line 2)', merchant.AddressLine2],
    ['City', merchant.City],
    ['Postal code', merchant.PostalCode],
  ];

  return (
    <section className="bo-card bo-detail-section">
      <div className="bo-merchant-company-head">
        {showLogo && logoUrl ? (
          <div className="bo-merchant-logo-shell">
            <MerchantLogo key={logoUrl} url={logoUrl} name={merchant.Name || '—'} />
          </div>
        ) : null}
        <div className="bo-merchant-company-titles">
          <p className="bo-detail-section-eyebrow">Merchant</p>
          <h2 className="bo-detail-section-title">{merchant.Name || '—'}</h2>
        </div>
      </div>
      <div className="bo-detail-fields-grid">
        {fields.map(([k, v]) => (
          <DetailRow key={k} label={k}>
            {formatScalar(v)}
          </DetailRow>
        ))}
      </div>
    </section>
  );
}

export function MerchantDetailPage(): React.ReactElement {
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();
  const { acquireAccessToken } = useAuth();
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    if (!merchantId) {
      setError('Missing merchant id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMerchantById(merchantId, acquireAccessToken);
      setMerchant(data);
    } catch (e) {
      setMerchant(null);
      setError(e instanceof Error ? e.message : 'Failed to load merchant.');
    } finally {
      setLoading(false);
    }
  }, [merchantId, acquireAccessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  useDocumentTitle(
    merchant
      ? `${recordDisplayName(merchant)} · Merchant`
      : 'Merchant · Triplicity Backoffice'
  );

  const handleEdit = (): void => {
    if (merchantId) {
      navigate(`/merchants/${encodeURIComponent(merchantId)}/edit`);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!merchantId) {
      throw new Error('Missing merchant id.');
    }
    await deleteMerchant(merchantId, acquireAccessToken);
    navigate('/merchants');
  };

  const label = merchant ? recordDisplayName(merchant) : merchantId ?? 'this record';

  return (
    <div className="bo-detail-page">
      <PageHeader
        title="Merchant detail"
        subtitle={merchantId ? `Id: ${merchantId}` : undefined}
        actions={
          <div className="bo-actions">
            <button type="button" className="bo-btn bo-btn-primary" onClick={handleEdit}>
              Edit
            </button>
            <button
              type="button"
              className="bo-btn bo-btn-danger"
              onClick={() => setDeleteOpen(true)}
              disabled={!merchant}
            >
              Delete
            </button>
          </div>
        }
      />

      <Link className="bo-detail-back-link" to="/merchants">
        ← Back to merchants
      </Link>

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading merchant…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && merchant ? (
        <div className="bo-detail-stack">
          <section className="bo-card bo-detail-section">
            <p className="bo-detail-section-eyebrow">Account</p>
            <h2 className="bo-detail-section-title">User profile</h2>
            <div className="bo-detail-fields-grid">
              <DetailRow label="ID">{formatScalar(merchant.Id)}</DetailRow>
              <DetailRow label="Email">{formatScalar(merchant.Email)}</DetailRow>
              <DetailRow label="First name">{formatScalar(merchant.FirstName)}</DetailRow>
              <DetailRow label="Last name">{formatScalar(merchant.LastName)}</DetailRow>
              <DetailRow label="Banned">{formatScalar(merchant.Banned)}</DetailRow>
              <DetailRow label="Create date">{formatScalar(merchant.CreateDate)}</DetailRow>
              <DetailRow label="Last login">{formatScalar(merchant.LastLogin)}</DetailRow>
              <DetailRow label="Merchant ID">{formatScalar(merchant.MerchantId)}</DetailRow>
            </div>
          </section>
          {merchant.Merchant ? <MerchantCompanyCard merchant={merchant.Merchant} /> : null}
        </div>
      ) : null}

      {!loading && !error && !merchant ? (
        <div className="bo-card">
          <p style={{ margin: 0 }}>Merchant not found.</p>
        </div>
      ) : null}

      <DeleteMerchantModal
        visible={deleteOpen}
        merchantLabel={label}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
